import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { getServicesManager } from '../../../../services';
import { startLoading, startDebouncing, stopDebouncing, sessionsLoaded, loadingFailed, toReduxSafeSessionListResponses } from './list-slice';
import { FilterOperator, SelectColumn, SessionListV5Params, SessionListResponse } from '../../../../interfaces/sessions';
import { BackendTables } from '../../../../interfaces/db';
import { SessionV2Columns, CallMetricColumns, MessagingDataColumns, PROSPECT_INFO_COLUMNS, ReviewStatusColumns, SESSION_METRICS_COLUMNS, CustomMetricColumns, CustomDetailColumns, SessionAgentsColumns, SessionStatesColumns } from '../../../../interfaces/table';
import { SessionDetailView, SessionHistoryTabs } from '../../../../components/History/views';
import { useSessionItems } from './session-items-hook';
import { RootState } from '../../../../store';
import { useTextsForNumbers } from '../texting/hooks';
import { PendingSend } from '../texting/slice';

type UseSessionsByNumbersProps = {
  numbers: string[];
  view?: SessionDetailView;
  selectedSessionId?: string;
}

// Export the combined item type
export type SessionByNumbersItem = {
  type: 'item' | 'pendingText';
  start: Date;
  data: SessionListResponse | PendingSend;
};

const DEFAULT_PAGE_SIZE = 50;
const INITIAL_LOOKBACK_MONTHS = 12; // Start with 1 year

export function useSessionsByNumbers({ numbers, view = SessionDetailView.EMBEDDED, selectedSessionId }: UseSessionsByNumbersProps) {
  // Memoize the numbers array to prevent re-fetching on every render
  const stableNumbers = useMemo(() => {
    return [...numbers].sort(); // Sort to ensure consistent ordering
  }, [numbers]); // Use numbers directly instead of JSON.stringify

  const dispatch = useDispatch();
  const sessionState = useSelector((state: RootState) => state.sessionList);
  const custom_definitions = useSelector((state: RootState) => state.customDefinitions.value);
  const has_custom_definitions = custom_definitions && custom_definitions.length > 0;
  
  // Listen to global refresh state
  const globalRefreshCount = useSelector((state: RootState) => state.refresh.forceRefresh);
  
  // Track previous refresh count to trigger fetch on refresh
  const previousRefreshCountRef = useRef<number>(0);
  
  // Use session-items-hook with phone number filter - use stable numbers
  const sessionItems = useSessionItems({
    passed_filter: [[{
      column_name: SessionV2Columns.CUSTOMER_ADDRESS,
      operator: FilterOperator.IN,
      value_safe: stableNumbers, // Use stable numbers
      table: BackendTables.SESSIONS_V2,
    }]],
    enforced_start: null,
    enforced_end: null,
    tab: SessionHistoryTabs.ALL_HISTORY,
    view,
    disableDebouncing: true
  });

  const { sessionKey, getCurrentSession } = sessionItems;

  // Create memoized selectors for texting data to prevent unnecessary re-renders
  const selectPendingTexts = useMemo(() => 
    createSelector(
      (state: RootState) => state.textingMessages.pendingSend,
      (state: RootState) => stableNumbers,
      (pendingSend, numbers) => pendingSend.filter((text) => numbers.includes(text.target_phone_number))
    ), [stableNumbers]);

  const selectSuccessfulTexts = useMemo(() => 
    createSelector(
      (state: RootState) => state.textingMessages.sent,
      (state: RootState) => stableNumbers,
      (sent, numbers) => sent.filter((text) => numbers.includes(text.target_phone_number))
    ), [stableNumbers]);

  // Use memoized selectors
  const pendingTexts = useSelector(selectPendingTexts);
  const successfulTexts = useSelector(selectSuccessfulTexts);
  const applicableTextSessions = useTextsForNumbers(stableNumbers); // Use stable numbers

  // Track incoming texts separately - detect when new customer messages arrive
  const incomingTexts = useMemo(() => {
    return applicableTextSessions.filter(session => {
      const customerBodies = session.result.get(MessagingDataColumns.CUSTOMER_BODIES_STR);
      return customerBodies && customerBodies !== '[]' && customerBodies !== '';
    });
  }, [applicableTextSessions]);

  // Derived texting data
  const stillPendingOrErrorTexts = useMemo(() => 
    pendingTexts.filter((text) => 
      !successfulTexts.some((v) => v.uuid === text.uuid) && text.status.status === 'failed'
    ), [pendingTexts, successfulTexts]);

  const notFetchedSuccessfulTexts = useMemo(() => 
    successfulTexts.filter((text) => 
      !applicableTextSessions.some((v) => v.session_id === text.session.session_id)
    ), [successfulTexts, applicableTextSessions]);

  const newRawTexts = useMemo(() => {
    return applicableTextSessions.map((v) => `${v.result.get(MessagingDataColumns.AGENT_BODIES_STR) ?? ''}: ${v.result.get(MessagingDataColumns.CUSTOMER_BODIES_STR) ?? ''}`)
  }, [applicableTextSessions]);

  // Refs to prevent duplicate fetches and manage progressive fetching
  const isFetchingRef = useRef<boolean>(false);
  const lastNumbersRef = useRef<string>('');
  const currentLookbackMonthsRef = useRef<number>(INITIAL_LOOKBACK_MONTHS);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Stable refs to prevent infinite rerenders
  const sessionKeyRef = useRef<string>('');
  const numbersRef = useRef<string[]>([]);
  const filterRef = useRef<any>(null);
  const columnsRef = useRef<SelectColumn[]>([]);

  // Column definitions (simplified for phone number fetching)
  const columns: SelectColumn[] = useMemo(() => {
    const required_columns: SelectColumn[] = [
      { column_name: SessionV2Columns.SESSION_ID, table: BackendTables.SESSIONS_V2 },
      { column_name: SessionV2Columns.STARTED_AT, table: BackendTables.SESSIONS_V2 },
      { column_name: SessionV2Columns.AGENT_ADDRESS, table: BackendTables.SESSIONS_V2 },
      { column_name: SessionV2Columns.CUSTOMER_ADDRESS, table: BackendTables.SESSIONS_V2 },
      { column_name: SessionV2Columns.RESOURCE_ID, table: BackendTables.SESSIONS_V2 },
      { column_name: SessionV2Columns.CONVERSATION_TYPE, table: BackendTables.SESSIONS_V2 },
      { column_name: SessionV2Columns.SESSION_TYPE, table: BackendTables.SESSIONS_V2 },
      { column_name: SESSION_METRICS_COLUMNS.PLATFORM, table: BackendTables.SESSION_METRICS },
      { column_name: SessionAgentsColumns.CREATOR_AGENT_ID, table: BackendTables.SESSION_AGENTS },
      { column_name: SessionAgentsColumns.FIRST_USER_ID, table: BackendTables.SESSION_AGENTS }, 
      { column_name: SessionAgentsColumns.AGENT_TYPES_STR, table: BackendTables.SESSION_AGENTS }
    ];

    // Add session state columns for all calls
    const session_state_columns: SelectColumn[] = [
      { column_name: SessionStatesColumns.STATE_STR, table: BackendTables.SESSION_STATES }
    ];

    const review_columns: SelectColumn[] = [
      { column_name: ReviewStatusColumns.IS_OPEN, table: BackendTables.REVIEW_STATUSES },
      { column_name: ReviewStatusColumns.OPENING_USER_ID, table: BackendTables.REVIEW_STATUSES }
    ];

    const prospect_columns: SelectColumn[] = [
      { column_name: PROSPECT_INFO_COLUMNS.PROSPECT_NAME, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.PROSPECT_TITLE, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.COMPANY_NAME, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.PLATFORM_COMPANY_ID, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_ID, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_URL, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.XREF_PERSON_ID, table: BackendTables.PROSPECT_INFO },
      { column_name: PROSPECT_INFO_COLUMNS.XREF_PERSON_URL, table: BackendTables.PROSPECT_INFO }
    ];

    const call_columns: SelectColumn[] = [
      { column_name: CallMetricColumns.CALL_DURATION, table: BackendTables.CALL_METRICS },
      { column_name: SESSION_METRICS_COLUMNS.LIVE_DURATION, table: BackendTables.SESSION_METRICS },
      { column_name: SESSION_METRICS_COLUMNS.IS_ANSWERED, table: BackendTables.SESSION_METRICS },
      { column_name: SESSION_METRICS_COLUMNS.GATEKEEPER_DURATION, table: BackendTables.SESSION_METRICS },
      { column_name: SESSION_METRICS_COLUMNS.TARGET_DURATION, table: BackendTables.SESSION_METRICS },
      { column_name: SESSION_METRICS_COLUMNS.BEST_COUNTERPART, table: BackendTables.SESSION_METRICS },
      { column_name: SESSION_METRICS_COLUMNS.GEN_SUMMARY_TEXT, table: BackendTables.SESSION_METRICS },
      { column_name: CallMetricColumns.FIRST_USER_JOINED_AT, table: BackendTables.CALL_METRICS },
      { column_name: CallMetricColumns.IS_INBOUND, table: BackendTables.CALL_METRICS },
      { column_name: SESSION_METRICS_COLUMNS.USER_DISPOSITION, table: BackendTables.SESSION_METRICS },
      { column_name: SESSION_METRICS_COLUMNS.USER_SENTIMENT, table: BackendTables.SESSION_METRICS },
    ];

    const text_columns: SelectColumn[] = [
      { column_name: MessagingDataColumns.AGENT_BODIES_STR, table: BackendTables.MESSAGING_DATA },
      { column_name: MessagingDataColumns.AGENT_TIMES_STR, table: BackendTables.MESSAGING_DATA },
      { column_name: MessagingDataColumns.CUSTOMER_BODIES_STR, table: BackendTables.MESSAGING_DATA },
      { column_name: MessagingDataColumns.CUSTOMER_TIMES_STR, table: BackendTables.MESSAGING_DATA },
      { column_name: MessagingDataColumns.MAX_CUSTOMER_AT, table: BackendTables.MESSAGING_DATA }
    ];

    const score_columns: SelectColumn[] = has_custom_definitions && (view === SessionDetailView.MAIN || view === SessionDetailView.ANALYSIS || view === SessionDetailView.ANALYTICS) ? [
      ...Object.values(CustomMetricColumns).map((column) => ({ column_name: column, table: BackendTables.CUSTOM_METRICS })),
      ...Object.values(CustomDetailColumns).map((column) => ({ column_name: column, table: BackendTables.CUSTOM_DETAILS }))
    ] : [];

    return [
      ...required_columns,
      ...session_state_columns,
      ...review_columns,
      ...prospect_columns,
      ...call_columns,
      ...text_columns,
      ...score_columns
    ];
  }, [has_custom_definitions, view]);

  // Update refs when values change
  useEffect(() => {
    sessionKeyRef.current = sessionKey;
  }, [sessionKey]);

  useEffect(() => {
    numbersRef.current = stableNumbers; // Use stable numbers
  }, [stableNumbers]);

  useEffect(() => {
    filterRef.current = sessionItems.filter;
  }, [sessionItems.filter]);

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  // Check if we have found the selected session
  const hasSelectedSession = useMemo(() => {
    if (!selectedSessionId) return true; // No specific session needed
    
    // Check in regular sessions
    const foundInSessions = sessionItems.items.some(item => item.session_id === selectedSessionId);
    if (foundInSessions) {
      return true;
    }
    
    // Check in text sessions
    const foundInTexts = applicableTextSessions.some(item => item.session_id === selectedSessionId);
    if (foundInTexts) {
      return true;
    }
    
    // Check in successful texts
    const foundInSuccessfulTexts = successfulTexts.some(item => item.session.session_id === selectedSessionId);
    if (foundInSuccessfulTexts) {
      return true;
    }
    
    // Check in pending texts
    const foundInPendingTexts = pendingTexts.some(item => item.session_id === selectedSessionId);
    if (foundInPendingTexts) {
      return true;
    }
    
    return false;
  }, [selectedSessionId, sessionItems.items, applicableTextSessions, successfulTexts, pendingTexts]);

  // Fetch function with proper pagination support
  const debouncedFetchSessions = useCallback((isLoadMore: boolean = false) => {
    // Clear any existing timeout for additional fetches
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    const executeNow = () => {
      performFetch(isLoadMore);
    };

    // Execute immediately for both initial loads and load more
    if (isLoadMore) {
      const currentSessionKey = sessionKeyRef.current;
      if (currentSessionKey) {
        dispatch(startDebouncing({ key: currentSessionKey }));
      }
      // Execute immediately for load more
      executeNow();
    } else {
      // Execute immediately for initial loads
      executeNow();
    }

    async function performFetch(isLoadMoreRequest: boolean) {
      const currentSessionKey = sessionKeyRef.current;
      const currentNumbers = numbersRef.current;
      const currentFilter = filterRef.current;
      const currentColumns = columnsRef.current;

      if (!currentSessionKey || currentNumbers.length === 0 || isFetchingRef.current) {
        if (currentSessionKey) {
          dispatch(stopDebouncing({ key: currentSessionKey }));
        }
        return;
      }

      const currentState = getCurrentSession(currentSessionKey);
      
      // For load more, check if we have more data to fetch
      if (isLoadMoreRequest && !currentState.hasMore) {
        dispatch(stopDebouncing({ key: currentSessionKey }));
        return;
      }

      const numbersString = JSON.stringify(currentNumbers.sort());
      const requestKey = `${numbersString}-${currentLookbackMonthsRef.current}-${isLoadMoreRequest ? 'loadmore-' + currentState.items.length : 'initial'}`;
      
      if (requestKey === lastNumbersRef.current && !isLoadMoreRequest) {
        dispatch(stopDebouncing({ key: currentSessionKey }));
        return; // Already fetched for these numbers
      }
      
      // For load more requests, always proceed (don't check for duplicates)

      isFetchingRef.current = true;
      lastNumbersRef.current = requestKey;

      try {
        dispatch(startLoading({ key: currentSessionKey, lastRequestKey: requestKey }));

        const lookbackMs = currentLookbackMonthsRef.current * 30 * 24 * 60 * 60 * 1000;
        let startDate = new Date(new Date().getTime() - lookbackMs);
        let endDate = new Date();
        
        // For load more requests, fetch older sessions (earlier dates)
        if (isLoadMoreRequest && currentState.items.length > 0) {
          // Find the oldest session (since items are sorted newest first in Redux, but chronologically in our hook)
          const oldestSession = currentState.items[currentState.items.length - 1];
          endDate = new Date(oldestSession.start.getTime() - 1); // End just before the oldest session
          // Keep the same lookback period from the oldest session
          startDate = new Date(endDate.getTime() - lookbackMs);
        }
        
        const params: SessionListV5Params = {
          cnf: currentFilter,
          select: currentColumns,
          start: startDate,
          end: endDate,
        };

        const result = await getServicesManager().getListUserSessionsV5(params);
        
        if (result) {
          const hasMore = result.length === DEFAULT_PAGE_SIZE;
          // Convert to Redux-safe format
          const reduxSafeResult = toReduxSafeSessionListResponses(result);
          // Get existing Redux-safe items from Redux state directly
          const reduxSession = sessionState.sessions[currentSessionKey];
          const existingItems = reduxSession?.items || [];
          
          dispatch(sessionsLoaded({
            key: currentSessionKey,
            items: isLoadMoreRequest ? [...existingItems, ...reduxSafeResult] : reduxSafeResult,
            startRequested: startDate ? startDate.toISOString() : null,
            startHasMore: hasMore,
            hasMore: hasMore,
            isForceRefresh: !isLoadMoreRequest,
            lastRequestKey: requestKey
          }));
        }
      } catch (error) {
        dispatch(loadingFailed({
          key: currentSessionKey,
          error: error instanceof Error ? error.message : 'Unknown error fetching sessions'
        }));
      } finally {
        isFetchingRef.current = false;
        dispatch(stopDebouncing({ key: currentSessionKey }));
      }
    }
  }, [dispatch, getCurrentSession, sessionState.sessions]); // Add sessionState.sessions as dependency

  // Track previous session key to detect when it changes due to refresh
  const previousSessionKeyRef = useRef<string>('');
  
  // Initial fetch when numbers change OR when session key changes (due to refresh) - use stable numbers
  useEffect(() => {
    // Capture the ref value at the beginning of the effect
    const timeoutId = debounceTimeoutRef.current;
    
    if (stableNumbers.length > 0) {
      // Check if we already have data for this session key (numbers combination)
      const currentState = getCurrentSession(sessionKey);
      
      // Detect if session key changed (due to refresh)
      const sessionKeyChanged = sessionKey !== previousSessionKeyRef.current;
      if (sessionKeyChanged) {
        previousSessionKeyRef.current = sessionKey;
      }
      
      // Only fetch if we don't already have items in Redux for this numbers combination
      // OR if the session key changed (indicating a refresh)
      if (currentState.items.length === 0 || sessionKeyChanged) {
        debouncedFetchSessions(false); // No debouncing for initial load or refresh
      }
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [stableNumbers, sessionKey, getCurrentSession, debouncedFetchSessions]); // Check Redux state directly

  // Progressive fetch when selectedSessionId is provided but not found
  useEffect(() => {
    // Only proceed if we have a specific session ID to find
    if (!selectedSessionId) return;
    
    // Don't search if we're currently loading or debouncing
    if (sessionItems.isLoading || sessionItems.isDebouncing) return;
    
    // Check if we've found the target session
    if (hasSelectedSession) {
      return;
    }
    
    // Check if we have items and can fetch more backwards
    if (sessionItems.items.length === 0) {
      // No items yet, initial fetch should handle this
      return;
    }
    
    // Check if we can fetch more (based on hasMore from the session items)
    if (!sessionItems.hasMore) {
      return;
    }
    
    // Debounce the progressive search to avoid rapid-fire requests
    const timeoutId = setTimeout(() => {
      // Double-check conditions after debounce delay
      if (!selectedSessionId || hasSelectedSession || sessionItems.isLoading || sessionItems.isDebouncing) {
        return;
      }
      
      // Fetch more historical data by using load more logic
      debouncedFetchSessions(true); // Use load more to fetch backwards
    }, 300); // 300ms debounce to avoid rapid requests
    
    return () => clearTimeout(timeoutId);
  }, [selectedSessionId, hasSelectedSession, sessionItems.isLoading, sessionItems.isDebouncing, sessionItems.items.length, sessionItems.hasMore, debouncedFetchSessions]);

  // Load more function
  const loadMore = useCallback(() => {
    debouncedFetchSessions(true);
  }, [debouncedFetchSessions]);

  // Simple refresh function (kept for manual refresh calls)
  const refresh = useCallback(() => {
    lastNumbersRef.current = ''; // Reset to force refetch
    currentLookbackMonthsRef.current = INITIAL_LOOKBACK_MONTHS; // Reset lookback
    debouncedFetchSessions(false); // No debouncing for refresh
  }, [debouncedFetchSessions]);

  // Handle global refresh count changes
  useEffect(() => {
    if (globalRefreshCount > previousRefreshCountRef.current) {
      previousRefreshCountRef.current = globalRefreshCount;
      refresh();
    }
  }, [globalRefreshCount, refresh]);

  // Add logging to see what we're getting
  return {
    // Combined and sorted data - ready to use as 'items'
    items: useMemo((): SessionByNumbersItem[] => {
      
      const sessionitems = sessionItems.items.map((item) => ({
        type: 'item' as const,
        start: item.start,
        data: item,
      }));
        
      const newTextitems = applicableTextSessions.map((item) => ({
        type: 'item' as const,
        start: item.start,
        data: item,
      }));
      
      const successfulTextsItems = notFetchedSuccessfulTexts.map((item) => ({
        type: 'item' as const,
        start: item.session.start,
        data: item.session,
      }));
      
      const pendingTextsItems = stillPendingOrErrorTexts.map((item) => ({
        type: 'pendingText' as const,
        start: new Date(item.start), // Convert ISO string to Date
        data: item,
      }));

      return [...sessionitems, ...newTextitems, ...successfulTextsItems, ...pendingTextsItems]
        .sort((a, b) => {
          const dateA = a.start
          const dateB = b.start
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA.getTime() - dateB.getTime(); // Ascending order (oldest first - chronological)
        });
        
    }, [sessionItems.items, applicableTextSessions, notFetchedSuccessfulTexts, stillPendingOrErrorTexts]),

    // Raw session data for transcript count
    sessionItems: sessionItems.items,
    // Raw text sessions for TextComposer (expects SessionListResponse[])
    rawTextSessions: applicableTextSessions,
    count: sessionItems.count,
    isLoading: sessionItems.isLoading,
    isDebouncing: sessionItems.isDebouncing,
    // Consolidated loading state that includes debouncing
    isLoadingOrDebouncing: sessionItems.isLoadingOrDebouncing,
    error: sessionItems.error,
    
    // Texting data - all consolidated here
    pendingTexts,
    successfulTexts,
    incomingTexts,
    applicableTextSessions,
    stillPendingOrErrorTexts,
    notFetchedSuccessfulTexts,
    newRawTexts,
    
    // Simple refresh function
    refresh,
    
    // Pagination support
    hasMore: sessionItems.hasMore,
    loadMore
  };
} 